import { File } from "expo-file-system";
import { getFallbackArtwork } from "./artworkFallback";

/**
 * Checks if a URL is a local/offline file path.
 */
function isOfflineUrl(url: string): boolean {
  return url.startsWith("file:") || url.includes("/downloads/");
}

/**
 * Validates an artwork URL before passing to AudioPro.
 * - For local files: checks existence synchronously, returns fallback if missing
 * - For remote URLs: trusts them (AudioPlayer will retry with fallback if they fail)
 *
 * AudioPro crashes if given an invalid local artwork path. Remote URLs that fail
 * are handled by AudioPlayer's retry mechanism (see retryWithFallbackArtwork).
 *
 * @param artworkUrl - The artwork URL to validate
 * @param songId - Song ID for deterministic fallback selection
 * @returns Valid artwork URL or fallback placeholder path (for local) or original URL (for remote)
 */
export async function validateArtworkUrl(
  artworkUrl: string | undefined,
  songId: string
): Promise<string> {
  if (!artworkUrl) {
    return getFallbackArtwork(songId);
  }

  // Local file path: check existence synchronously
  if (isOfflineUrl(artworkUrl)) {
    try {
      const file = new File(artworkUrl);
      if (!file.exists) {
        const fallback = await getFallbackArtwork(songId);
        console.audio.warn(
          "Local artwork missing:",
          artworkUrl,
          "-> fallback:",
          fallback
        );
        return fallback;
      }
      return artworkUrl;
    } catch (error) {
      console.audio.warn("Error checking local artwork:", error);
      return getFallbackArtwork(songId);
    }
  }

  // Remote URLs: trust them, let AudioPlayer retry with fallback if they fail
  return artworkUrl;
}
